"use client";

import { getBaseApiClient } from "@newxsystems/api-base/client";
import { getLatestBlogPostsQueryKey } from "@newxsystems/api-base/keys";
import { cn } from "@newxsystems/shared";
import { useQuery } from "@tanstack/react-query";
import { KaajBlogPostCard } from "../../../../modules/blog/components/KaajBlogPostCard/KaajBlogPostCard";
import { KaajSectionShell } from "../../../../modules/layout/components/KaajSectionShell/KaajSectionShell";
import type { ILatestBlogPostsBlockProps } from "./LatestBlogPostsBlock.props";

const DEFAULT_HEADING = "Latest intelligence";
const DEFAULT_EMPTY_TEXT = "No analysis has been published yet.";

export const LatestBlogPostsBlockClient = (props: ILatestBlogPostsBlockProps) => {
  const { anchorId, eyebrow, spacing, subheading } = props;
  const heading = Object.hasOwn(props, "heading") ? props.heading : DEFAULT_HEADING;
  const emptyText = Object.hasOwn(props, "emptyText") ? props.emptyText : DEFAULT_EMPTY_TEXT;
  const limit = props.limit;
  const { data, isLoading } = useQuery({
    queryKey: getLatestBlogPostsQueryKey(limit),
    queryFn: async () => {
      const client = getBaseApiClient();
      return client ? client.blog.latest({ limit }) : { items: [] };
    },
  });
  const posts = (data?.items ?? []).slice(0, limit);

  return (
    <KaajSectionShell
      id={anchorId ?? undefined}
      eyebrow={eyebrow ?? undefined}
      heading={heading ?? undefined}
      spacing={spacing ?? "normal"}
      subheading={subheading ?? undefined}
    >
      {posts.length ? (
        <ul
          className={cn(
            "grid list-none gap-5 p-0 lg:gap-6",
            limit === 4 ? "md:grid-cols-2 xl:grid-cols-4" : "md:grid-cols-2 xl:grid-cols-3",
          )}
        >
          {posts.map((post, index) => (
            <li className="min-w-0" key={post.id}>
              <KaajBlogPostCard post={post} priority={index < 3} />
            </li>
          ))}
        </ul>
      ) : (
        <div
          aria-live="polite"
          className="rounded-[1.5rem] border border-dashed border-[#AEBEFF] bg-white p-6 text-[#4B5568]"
        >
          {isLoading ? "Loading the latest analysis…" : emptyText}
        </div>
      )}
    </KaajSectionShell>
  );
};

LatestBlogPostsBlockClient.displayName = "Block::LatestBlogPostsBlockClient";
